import * as React from "react";
import { Modal } from "antd";
import { useFormContext } from "react-hook-form";
import { NGButton } from "../../newcomponents/button/Button";
import { FormDryRunAlert } from "./create_ng/formDryRunAlert";
import { NGLabelText } from "../../newcomponents/text/labelText";
import { MinusCircleOutlined } from "@ant-design/icons";
import { FormNGInput } from "./create_ng/components/FormNGInput";
import { DomainInput, DomainPortInput } from "./create_ng/types/input";
import { v4 as uuidv4 } from "uuid";
import NGAlert from "../../newcomponents/alert";

interface Props {
  portId: string;
  portIndex: number;
  onClose: () => void;
}
export const DomainPortRouteEditHeadersModal: React.FC<Props> = ({ portId, portIndex, onClose }) => {
  const { control, watch, setValue, getValues } = useFormContext<DomainInput>();
  const fPorts: DomainPortInput[] = watch("advanced.spec.ports");
  const fPort: DomainPortInput = React.useMemo(() => {
    return fPorts.find((p) => p.id === portId)!;
  }, [fPorts, portId]);

  if (!fPort) {
    return null;
  }

  return (
    <Modal
      open={true}
      styles={{ body: { height: "70vh", padding: 0 } }}
      width={1000}
      title={`Edit Route Headers for Port ${fPort.number}`}
      onCancel={() => {}}
      maskClosable={false}
      closable={false}
      footer={
        <div className="modal-actions">
          <NGButton
            variant="primary"
            onClick={() => {
              onClose();
            }}
          >
            OK
          </NGButton>
        </div>
      }
    >
      <div className="h-full overflow-auto">
        {fPort.routes.map((route, routeIndex) => {
          return (
            <div key={route.id}>
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-2">
                  <NGLabelText className="cursor-default">{`Workload: ${route.workload.name} ${
                    route.hostType === "prefix" ? `Prefix: ${route.prefix}` : `Regex: ${route.regex}`
                  }`}</NGLabelText>
                  <NGButton
                    variant="primary"
                    size={"small"}
                    onClick={() => {
                      const headers = getValues(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers`);
                      setValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers` as const, [
                        ...headers,
                        { id: uuidv4(), name: "", value: "" },
                      ]);
                    }}
                  >
                    Add
                  </NGButton>
                </div>
                {route.headers.length < 1 ? <div>No Items</div> : null}
                {route.headers.map((header, headerIndex) => {
                  const previousHeaders = route.headers.slice(0, headerIndex);
                  const hasSameName =
                    header.name && previousHeaders.some((ph) => ph.name.toLowerCase() === header.name.toLowerCase());

                  return (
                    <>
                      <div key={headerIndex} className="flex items-center gap-2 mb-1">
                        <FormNGInput
                          name={
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers.${headerIndex}.name` as const
                          }
                          control={control}
                          required={true}
                          style={{ minWidth: 450, maxWidth: 450 }}
                        />
                        <FormNGInput
                          name={
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers.${headerIndex}.value` as const
                          }
                          control={control}
                          required={true}
                          style={{ minWidth: 450, maxWidth: 450 }}
                        />
                        <MinusCircleOutlined
                          className="color-danger"
                          onClick={() => {
                            const headers = getValues(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers`);
                            const newHeaders = headers.filter((_, idx) => idx !== headerIndex);
                            setValue(
                              `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers` as const,
                              newHeaders,
                            );
                          }}
                        />
                      </div>
                      {hasSameName ? (
                        <NGAlert type={`warning`} message={`Overrides the previous header with the same name`} />
                      ) : null}
                    </>
                  );
                })}
                <FormDryRunAlert
                  path={`spec.ports[${portIndex}].routes[${routeIndex}].headers`}
                  paths={[
                    `spec.ports[${portIndex}].routes[${routeIndex}].headers.request`,
                    `spec.ports[${portIndex}].routes[${routeIndex}].headers.request.set`,
                    ...route.headers.map(
                      (h) => `spec.ports[${portIndex}].routes[${routeIndex}].headers.request.set.${h.name}`,
                    ),
                  ]}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
